'use client'
import React from 'react'

import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import { MixerHorizontalIcon } from '@radix-ui/react-icons'
import { Column, Table } from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu'

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>
}

const renderColumnTitle = (column: Column<any, any>) => {
  const headerDef = column.columnDef.header

  if (typeof headerDef === 'function') {
    const headerContent = headerDef({ column } as any)

    return typeof headerContent === 'string'
      ? headerContent
      : headerContent?.props?.title || column.id
  } else {
    return headerDef
  }
}

export function DataTableViewOptions<TData>({
  table,
}: DataTableViewOptionsProps<TData>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <MixerHorizontalIcon className="mr-2 h-4 w-4" />
          View
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {table
          .getAllColumns()
          .filter((column) => column.getCanHide())
          .map((column) => {
            const isGroup = column.columns?.length > 0
            return (
              <React.Fragment key={column.id}>
                {/* Toggle Group Column */}
                <DropdownMenuCheckboxItem
                  className="capitalize"
                  checked={
                    isGroup
                      ? column.getLeafColumns().every((c) => c.getIsVisible())
                      : column.getIsVisible()
                  }
                  onCheckedChange={(value) => {
                    if (isGroup) {
                      // Toggle visibility of all leaf columns in the group
                      column
                        .getLeafColumns()
                        .forEach((leaf) => leaf.toggleVisibility(!!value))
                    } else {
                      column.toggleVisibility(!!value)
                    }
                  }}
                >
                  {renderColumnTitle(column)}
                </DropdownMenuCheckboxItem>
                {isGroup &&
                  column.getIsVisible() &&
                  column.columns.map((subColumn) => (
                    <DropdownMenuCheckboxItem
                      key={subColumn.id}
                      className="ml-4 capitalize"
                      checked={subColumn.getIsVisible()}
                      onCheckedChange={(value) =>
                        subColumn.toggleVisibility(!!value)
                      }
                    >
                      {renderColumnTitle(subColumn)}
                    </DropdownMenuCheckboxItem>
                  ))}
              </React.Fragment>
            )
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
